import type { MaterialDefinition, ResourceAmount } from "@/lib/crafting/types";
import { assetUrl } from "@/lib/bungie/assets";

export interface ResourceLineProps {
  amount: ResourceAmount;
  materials: Record<string, MaterialDefinition>;
  /** Owned quantity, when the player's inventory is known. */
  owned?: number;
}

/**
 * One resource row. The icon is decorative — the name is always present as text
 * so the requirement is never conveyed by an image alone.
 */
export function ResourceLine({ amount, materials, owned }: ResourceLineProps) {
  const material = materials[String(amount.itemHash)];
  const name = material?.name ?? `Item ${amount.itemHash}`;
  const icon = assetUrl(material?.icon);
  const short = owned !== undefined && owned < amount.quantity;

  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="flex min-w-0 items-center gap-2">
        {icon ? (
          <img src={icon} alt="" aria-hidden className="h-5 w-5 shrink-0" />
        ) : (
          <span aria-hidden className="h-5 w-5 shrink-0 bg-white/10" />
        )}
        <span className="truncate text-material">{name}</span>
      </span>
      <span className="shrink-0 tabular-nums">
        {owned !== undefined && (
          <span className={short ? "text-short" : "text-ok"}>
            {owned.toLocaleString()}
            <span className="mx-1 text-ink-3">/</span>
          </span>
        )}
        <span className="font-semibold">{amount.quantity.toLocaleString()}</span>
        {owned !== undefined && (
          <span className="ml-2 text-xs">
            {short ? (
              <span className="text-short">
                short {(amount.quantity - owned).toLocaleString()}
              </span>
            ) : (
              <span className="text-ok">enough</span>
            )}
          </span>
        )}
      </span>
    </div>
  );
}
